import { type FormEvent, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../lib/api-client";
import { queryKeys } from "../lib/queryClient";
import { POLL } from "../lib/polling";
import { ErrorBanner, LoadingState, describeError } from "./States";
import { formatDateTime } from "../lib/format";

/** Seller-only stock management for a LICENSE_KEY-fulfillment listing — paste codes (one per
 *  line), see how many are left, and review what's already been claimed. Works regardless of the
 *  listing's status, so a live, even sold-out, listing can be restocked without pulling it. */
export function LicenseKeyManager({
  listingId,
  availableCount,
}: {
  listingId: string;
  /** Authoritative remaining stock from the listing response — the table below only shows the
   *  most recent page of keys for the seller's own record-keeping, so it isn't a reliable count
   *  once there are more keys than fit on one page. */
  availableCount: number;
}) {
  const queryClient = useQueryClient();
  const [pasted, setPasted] = useState("");

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: queryKeys.licenseKeys(listingId),
    queryFn: ({ signal }) => apiClient.getLicenseKeys(listingId, { limit: 100 }, signal),
    refetchInterval: POLL.list,
  });

  const addKeys = useMutation({
    mutationFn: (codes: string[]) => apiClient.addLicenseKeys(listingId, { codes }),
    onSuccess: () => {
      setPasted("");
      void queryClient.invalidateQueries({ queryKey: queryKeys.licenseKeys(listingId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.sellerListings });
    },
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const codes = pasted
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);
    if (codes.length === 0) return;
    addKeys.mutate(codes);
  }

  return (
    <div className="card stack">
      <h2>License keys</h2>
      <p className="text-sm muted">
        Each buyer automatically gets one unused key the moment their order is funded — no action
        needed from you. Paste one code per line to add stock.
      </p>

      <form className="stack" onSubmit={handleSubmit}>
        <div className="field">
          <label htmlFor="license-keys-paste">Add keys (one per line)</label>
          <textarea
            id="license-keys-paste"
            rows={5}
            maxLength={100_000}
            value={pasted}
            onChange={(e) => setPasted(e.target.value)}
            placeholder={"XXXX-XXXX-XXXX\nYYYY-YYYY-YYYY"}
          />
        </div>
        <button
          type="submit"
          className="btn btn-secondary"
          disabled={!pasted.trim() || addKeys.isPending}
        >
          {addKeys.isPending ? "Adding…" : "Add keys"}
        </button>
        {addKeys.isError && (
          <p className="field-error-text" role="alert">
            {describeError(addKeys.error)}
          </p>
        )}
        {addKeys.isSuccess && <p className="text-sm muted">Added {addKeys.data.added} key(s).</p>}
      </form>

      {isLoading && <LoadingState label="Loading keys…" />}
      {isError && <ErrorBanner error={error} onRetry={() => void refetch()} />}
      <p>
        <strong>{availableCount}</strong> {availableCount === 1 ? "key" : "keys"} available
      </p>

      {data && (
        <>
          {data.items.length > 0 && (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Status</th>
                    <th>Added</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((key) => (
                    <tr key={key.id}>
                      <td className="mono text-sm">{key.code}</td>
                      <td>{key.status}</td>
                      <td>{formatDateTime(key.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
